import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { api } from '../state/auth.js';
import { translateError } from '../i18n.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { openFile } from '../utils/openFile.js';

// Rapportage voor gemeente of eigenaar (sportcomplex fase 3): over a period the storingen
// the complex handled and the onderhoud en keuringen, on screen and as a PDF.

const ERRORS = { invalid_period: 'Kies een geldige periode (van voor tot).', period_too_long: 'Kies een periode van hooguit drie jaar.' };
const message = err => ERRORS[err.code] || translateError(err.code) || 'Dat lukte niet.';
const MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const fmt = d => (d ? `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]} ${d.slice(0, 4)}` : '');
const pad = n => String(n).padStart(2, '0');
const lastDay = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate(); // m: 1-12
const URGENCY = { gevaarlijk: 'Gevaarlijk', dringend: 'Dringend', normaal: 'Normaal' };
const KIND = { keuring: 'Keuring', onderhoud: 'Onderhoud' };
const muted = 'font-size:12px;color:var(--color-text-muted)';

// Quarter q (1-4) of year y; the preset periods
function quarter(y, q) { const m = (q - 1) * 3 + 1; return { from: `${y}-${pad(m)}-01`, to: `${y}-${pad(m + 2)}-${pad(lastDay(y, m + 2))}` }; }
function presets() {
  const n = new Date();
  const y = n.getFullYear();
  const q = Math.floor(n.getMonth() / 3) + 1;
  const prev = q === 1 ? quarter(y - 1, 4) : quarter(y, q - 1);
  return [
    ['dit_kwartaal', 'Dit kwartaal', quarter(y, q)],
    ['vorig_kwartaal', 'Vorig kwartaal', prev],
    ['dit_jaar', 'Dit jaar', { from: `${y}-01-01`, to: `${y}-12-31` }],
    ['vorig_jaar', 'Vorig jaar', { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` }],
  ];
}

function Figure({ label, value, bad }) {
  return (
    <div style="flex:1 1 130px;border:1px solid var(--color-border);border-radius:10px;padding:10px 12px">
      <div style={`font-size:22px;font-weight:700;${bad && value > 0 ? 'color:#b91c1c' : ''}`}>{value ?? '—'}</div>
      <div style={muted}>{label}</div>
    </div>
  );
}
const Counts = ({ title, rows, label = r => r.name }) => (
  <div style="flex:1 1 200px;min-width:0">
    <div style="font-weight:600;font-size:13px;margin-bottom:4px">{title}</div>
    {rows.length === 0 ? <div style={muted}>—</div> : rows.map(r => (
      <div key={r.name} style="display:flex;justify-content:space-between;font-size:13px;padding:2px 0;border-bottom:1px solid var(--color-border)"><span>{label(r)}</span><strong>{r.count}</strong></div>
    ))}
  </div>
);

export default function ComplexReportView() {
  const options = presets();
  const [preset, setPreset] = useState('dit_kwartaal');
  const [period, setPeriod] = useState(options[0][2]);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const query = `from=${period.from}&to=${period.to}`;
  // Only the answer for the period on screen counts (a slower earlier one doesn't overwrite it)
  const current = useRef(query);
  useEffect(() => {
    current.current = query;
    setReport(null); setError(null);
    api(`/api/maintenance/report?${query}`)
      .then(r => { if (current.current === query) setReport(r); })
      .catch(err => { if (current.current === query) setError(message(err)); });
  }, [query]);
  const choose = key => { setPreset(key); const p = options.find(o => o[0] === key); if (p) setPeriod(p[2]); };
  const s = report?.storingen;
  const o = report?.onderhoud;
  return (
    <div>
      <div class="page-header" style="margin-bottom:12px">
        <div>
          <h1 class="page-title">Rapportage</h1>
          <div style={muted}>Storingen en onderhoud over een periode, voor gemeente of eigenaar</div>
        </div>
        <Button disabled={!report} onClick={() => openFile(`/api/maintenance/report.pdf?${query}`, `Rapportage ${period.from} tot ${period.to}.pdf`)}>📄 PDF</Button>
      </div>
      <Card style="margin-bottom:16px">
        <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:center">
          {options.map(([key, label]) => <Button key={key} size="sm" variant={preset === key ? 'primary' : 'secondary'} onClick={() => choose(key)}>{label}</Button>)}
          <Button size="sm" variant={preset === 'zelf' ? 'primary' : 'secondary'} onClick={() => setPreset('zelf')}>Zelf kiezen</Button>
        </div>
        {preset === 'zelf' && (
          <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:10px">
            <label class="form-label" style="flex:1 1 150px">Van<input class="form-input" type="date" value={period.from} onInput={e => e.target.value && setPeriod(p => ({ ...p, from: e.target.value }))} /></label>
            <label class="form-label" style="flex:1 1 150px">Tot en met<input class="form-input" type="date" value={period.to} onInput={e => e.target.value && setPeriod(p => ({ ...p, to: e.target.value }))} /></label>
          </div>
        )}
        <div style={`${muted};margin-top:8px`}>{fmt(period.from)} tot en met {fmt(period.to)}</div>
      </Card>
      {error && <div class="alert alert-error mb-4">{error}</div>}
      {!report && !error && <div class="text-muted" style="padding:40px;text-align:center">Laden…</div>}
      {report && (
        <>
          <Card title="Storingen" style="margin-bottom:16px">
            <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:12px">
              <Figure label="gemeld" value={s.total} />
              <Figure label="opgelost" value={s.solved} />
              <Figure label="dagen tot opgelost (gemiddeld)" value={s.mean_days} />
              <Figure label="dagen (middelste)" value={s.median_days} />
              <Figure label="nog open aan het einde" value={s.open_at_end} bad />
            </div>
            <div style="display:flex;gap:16px;flex-wrap:wrap">
              <Counts title="Per urgentie" rows={s.by_urgency} label={r => URGENCY[r.name]} />
              <Counts title="Per plek" rows={s.by_location} />
              <Counts title="Per club" rows={s.by_club} />
            </div>
            {s.open_list.length > 0 && (
              <div style="margin-top:12px">
                <div style="font-weight:600;font-size:13px;margin-bottom:4px">Nog open</div>
                {s.open_list.map(r => <div key={r.id} style="font-size:13px;padding:2px 0">{r.title} <span style={muted}>· {[r.location, r.club, URGENCY[r.urgency], `gemeld ${fmt(r.created_on)}`].filter(Boolean).join(' · ')}</span></div>)}
                {s.open_at_end > s.open_list.length && <div style={muted}>… en nog {s.open_at_end - s.open_list.length}</div>}
              </div>
            )}
          </Card>
          <Card title="Onderhoud en keuringen">
            <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:12px">
              <Figure label="uitgevoerd" value={o.done} />
              <Figure label="op tijd" value={o.on_time} />
              <Figure label="te laat" value={o.late} bad />
              <Figure label={report.as_of < report.to ? 'achterstallig vandaag' : 'achterstallig aan het einde'} value={o.overdue_at_end} bad />
            </div>
            <div style="font-weight:600;font-size:13px;margin-bottom:4px">Uitgevoerd</div>
            {o.done_list.length === 0 && <div style={muted}>Niets uitgevoerd in deze periode.</div>}
            {o.done_list.map((r, i) => <div key={i} style="font-size:13px;padding:2px 0">{fmt(r.done_on)} · {KIND[r.kind]} {r.name}{r.location ? ` (${r.location})` : ''}{r.performed_by ? ` · ${r.performed_by}` : ''}{!r.on_time && <span style="color:#b91c1c"> · te laat</span>}</div>)}
            <div style="font-weight:600;font-size:13px;margin:10px 0 4px">{report.as_of < report.to ? 'Achterstallig vandaag' : 'Achterstallig aan het einde'}</div>
            {o.overdue_list.length === 0 && <div style={muted}>Niets achterstallig.</div>}
            {o.overdue_list.map((r, i) => <div key={i} style="font-size:13px;padding:2px 0">{KIND[r.kind]} {r.name}{r.location ? ` (${r.location})` : ''} · gepland {fmt(r.due_on)}</div>)}
          </Card>
        </>
      )}
    </div>
  );
}
